import { signal, computed } from '@angular/core';

/** Shown on Run controls while the execution runtimes are still loading. */
export const RUNTIME_PREPARING_MESSAGE =
  'Preparing execution environment - this step can run in a moment';

/**
 * Readiness barrier for the execution runtimes (Pyodide, DuckDB, the JS
 * runner). Everything needed only to *run* code is loaded after first paint
 * (EHQ-91), so every code-execution entry point must wait here first.
 *
 * The barrier is a single promise settled by the two `mark*` calls rather than
 * a polling loop. The previous implementation polled via
 * `GeneralHelpers.waitFor(cond, timeoutMs, 100)`, but that helper's signature
 * is `(cond, intervalMs, timeoutMs)` - the arguments were transposed, so it
 * polled once every 60s with a 100ms deadline and therefore *always* rejected
 * on the first tick. Both rejections were swallowed, so `waitForReadiness()`
 * silently fell through after ~600ms and never actually gated anything. That
 * is the stall the old TODO described.
 */
export class HeavyServicesReadinessHelper {
  /** Hard ceiling on how long a run may wait before proceeding anyway. */
  private static readonly DEFAULT_TIMEOUT_MS = 120000;

  private static readonly heavyServicesSignal = signal(false);
  private static readonly bootstrapCompleteSignal = signal(false);

  /** Ready once the runtimes have loaded *and* app context exists. */
  static readonly isReady = computed(() =>
    this.heavyServicesSignal() && this.bootstrapCompleteSignal()
  );

  /**
   * True while the execution runtimes are still loading. Bound by Run controls
   * to disable them and explain why (EHQ-91).
   */
  static readonly isPreparing = computed(() => !this.isReady());

  /** Resolver for `readyPromise`, cleared once the barrier opens. */
  private static resolveReady: (() => void) | undefined;

  /** Settled exactly once, when both prerequisites are met. */
  private static readyPromise: Promise<void> = new Promise<void>((resolve) => {
    HeavyServicesReadinessHelper.resolveReady = resolve;
  });

  /**
   * Open the barrier if both prerequisites are now satisfied. Idempotent -
   * later calls are no-ops because the resolver is dropped on first use.
   */
  private static settleIfReady(): void {
    if (!this.isReady() || !this.resolveReady) {
      return;
    }
    const resolve = this.resolveReady;
    this.resolveReady = undefined;
    resolve();
  }

  /** Mark the execution runtimes (Python, JS, DuckDB) as loaded. */
  static markHeavyServicesReady(): void {
    console.log('🎉 Execution runtimes marked as ready');
    this.heavyServicesSignal.set(true);
    this.settleIfReady();
  }

  /** Mark app bootstrap as complete (initial context loaded). */
  static markBootstrapComplete(): void {
    console.log('✅ App bootstrap marked as complete');
    this.bootstrapCompleteSignal.set(true);
    this.settleIfReady();
  }

  /**
   * Block until the execution runtimes are ready.
   *
   * Resolves immediately when already ready. On timeout it logs diagnostics and
   * resolves anyway - a slow runtime should degrade into a possibly-failing run
   * rather than a permanently hung Run button.
   */
  static async waitForReadiness(timeoutMs: number = this.DEFAULT_TIMEOUT_MS): Promise<void> {
    if (this.isReady()) {
      return;
    }

    console.log('⏳ Waiting for execution runtimes to finish loading...');
    const startedAt = performance.now();

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timedOut = await Promise.race([
      this.readyPromise.then(() => false),
      new Promise<boolean>((resolve) => {
        timer = setTimeout(() => resolve(true), timeoutMs);
      }),
    ]);
    if (timer !== undefined) {
      clearTimeout(timer);
    }

    const waitedMs = Math.round(performance.now() - startedAt);

    if (timedOut) {
      console.error(
        `💥 Execution runtimes not ready after ${timeoutMs}ms - proceeding anyway, ` +
        `this run may fail. Status:`,
        this.getStatus()
      );
      return;
    }

    console.log(`✅ Execution runtimes ready after ${waitedMs}ms`);
  }

  /** Current readiness status, for diagnostics. */
  static getStatus(): { heavyServices: boolean; bootstrap: boolean; overall: boolean } {
    return {
      heavyServices: this.heavyServicesSignal(),
      bootstrap: this.bootstrapCompleteSignal(),
      overall: this.isReady(),
    };
  }

  /** Reset all state (testing only). */
  static reset(): void {
    this.heavyServicesSignal.set(false);
    this.bootstrapCompleteSignal.set(false);
    this.readyPromise = new Promise<void>((resolve) => {
      this.resolveReady = resolve;
    });
  }
}
